import React, { useState } from 'react';
import styled from '@emotion/styled';

const StepContainer = styled.div`
  max-width: 800px;
  margin: 0 auto;
`;

const Section = styled.div`
  margin-bottom: 48px;
`;

const SectionTitle = styled.h3`
  font-size: 20px;
  font-weight: 600;
  color: #111827;
  margin: 0 0 24px;
  text-align: center;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 32px;
`;

const Label = styled.label`
  font-size: 14px;
  font-weight: 600;
  color: #374151;
`;

const Input = styled.input`
  padding: 12px 16px;
  border: 2px solid #e5e7eb;
  border-radius: 8px;
  font-size: 16px;
  transition: border-color 0.3s ease;

  &:focus {
    outline: none;
    border-color: #ff6b85;
  }

  &::placeholder {
    color: #9ca3af;
  }
`;

const RequiredIndicator = styled.span`
  color: #ef4444;
  margin-left: 4px;
`;

const PlansGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 24px;
  margin: 24px 0;
  
  @media (min-width: 768px) {
    grid-template-columns: repeat(3, 1fr);
  }
`;

const PlanCard = styled.div<{ selected: boolean; recommended?: boolean }>`
  border: 2px solid ${props => props.selected ? '#ff6b85' : '#e5e7eb'};
  border-radius: 12px;
  padding: 32px 24px;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
  background: ${props => props.selected ? '#fef7f7' : 'white'};

  &:hover {
    border-color: #ff6b85;
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(255, 107, 133, 0.15);
  }

  ${props => props.recommended && `
    &::before {
      content: 'Recommended';
      position: absolute;
      top: -10px;
      left: 50%;
      transform: translateX(-50%);
      background: linear-gradient(135deg, #ff8a95, #ff6b85, #ff5577);
      color: white;
      padding: 4px 16px;
      border-radius: 12px;
      font-size: 12px;
      font-weight: 600;
    }
  `}
`;

const PlanName = styled.h4`
  font-size: 24px;
  font-weight: 700;
  color: #333;
  margin: 0 0 8px;
`;

const PlanPrice = styled.div`
  font-size: 36px;
  font-weight: 700;
  color: #ff6b85;
  margin-bottom: 8px;

  span {
    font-size: 16px;
    color: #666;
    font-weight: 400;
  }
`;

const PlanDescription = styled.p`
  color: #666;
  font-size: 14px;
  margin: 0 0 24px;
`;

const FeatureList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  text-align: left;
`;

const Feature = styled.li`
  padding: 8px 0;
  color: #555;
  font-size: 14px;
  display: flex;
  align-items: center;
  gap: 8px;

  &::before {
    content: '✓';
    color: #10b981;
    font-weight: bold;
    font-size: 16px;
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  gap: 16px;
  justify-content: center;
  margin-top: 40px;
`;

const Button = styled.button<{ variant?: 'primary' | 'secondary' }>`
  padding: 16px 32px;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  border: none;
  cursor: pointer;
  transition: all 0.3s ease;
  min-width: 120px;

  ${props => props.variant === 'primary' ? `
    background: linear-gradient(135deg, #ff8a95, #ff6b85, #ff5577);
    color: white;
    
    &:hover:not(:disabled) {
      transform: translateY(-1px);
      box-shadow: 0 4px 12px rgba(255, 107, 133, 0.4);
    }

    &:disabled {
      opacity: 0.6;
      cursor: not-allowed;
      transform: none;
      box-shadow: none;
    }
  ` : `
    background: #f3f4f6;
    color: #374151;
    
    &:hover {
      background: #e5e7eb;
    }
  `}
`;

const SkipButton = styled.button`
  background: none;
  border: none;
  color: #6b7280;
  cursor: pointer;
  text-decoration: underline;
  margin-top: 16px;
  font-size: 14px;

  &:hover {
    color: #374151;
  }
`;

interface OnboardingStepProps {
  data: { plan?: 'free' | 'starter' | 'pro'; companyName?: string };
  onNext: (data: { plan: 'free' | 'starter' | 'pro'; companyName: string }) => void;
  onSkip?: () => void;
  isLoading?: boolean;
}

const plans = [
  {
    id: 'free',
    name: 'Free',
    price: '$0',
    period: '/month',
    description: 'Perfect for getting started',
    features: [
      '1 landing page',
      '100 leads per month',
      'Basic analytics',
      'Community support',
      'Chala Flow subdomain'
    ]
  },
  {
    id: 'starter',
    name: 'Starter',
    price: '$5',
    period: '/month',
    description: 'Perfect for small businesses',
    recommended: true,
    features: [
      'Up to 5 landing pages',
      '1,000 leads per month',
      'Advanced analytics',
      'Email support',
      'Custom subdomain',
      'Remove Chala branding'
    ]
  },
  {
    id: 'pro',
    name: 'Professional',
    price: '$10',
    period: '/month',
    description: 'Great for growing businesses',
    features: [
      'Up to 25 landing pages',
      '10,000 leads per month',
      'Advanced analytics',
      'Priority support',
      'Custom domain',
      'DNS setup ($400 one-time)',
      'A/B testing',
      'Integration tools',
      'Team collaboration'
    ]
  }
];

const OnboardingStep: React.FC<OnboardingStepProps> = ({
  data,
  onNext,
  onSkip,
  isLoading
}) => {
  const [selectedPlan, setSelectedPlan] = useState<'free' | 'starter' | 'pro' | ''>(data.plan || '');
  const [companyName, setCompanyName] = useState(data.companyName || '');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (selectedPlan && companyName.trim() && (selectedPlan === 'free' || selectedPlan === 'starter' || selectedPlan === 'pro')) {
      onNext({
        plan: selectedPlan,
        companyName: companyName.trim()
      });
    }
  };

  const isValid = selectedPlan && companyName.trim();

  return (
    <StepContainer>
      <form onSubmit={handleSubmit}>
        <Section>
          <SectionTitle>Company Information</SectionTitle>
          <FormGroup>
            <Label>
              Company Name
              <RequiredIndicator>*</RequiredIndicator>
            </Label>
            <Input
              type="text"
              value={companyName}
              onChange={(e) => setCompanyName(e.target.value)}
              placeholder="Enter your company name"
              required
            />
          </FormGroup>
        </Section>

        <Section>
          <SectionTitle>Choose Your Plan</SectionTitle>
          <PlansGrid>
            {plans.map((plan) => (
              <PlanCard
                key={plan.id}
                selected={selectedPlan === plan.id}
                recommended={plan.recommended}
                onClick={() => setSelectedPlan(plan.id as 'free' | 'starter' | 'pro')}
              >
                <PlanName>{plan.name}</PlanName>
                <PlanPrice>
                  {plan.price}
                  <span>{plan.period}</span>
                </PlanPrice>
                <PlanDescription>{plan.description}</PlanDescription>
                <FeatureList>
                  {plan.features.map((feature, index) => (
                    <Feature key={index}>{feature}</Feature>
                  ))}
                </FeatureList>
              </PlanCard>
            ))}
          </PlansGrid>
        </Section>

        <ButtonContainer>
          <Button
            type="submit"
            variant="primary"
            disabled={!isValid || isLoading}
          >
            {isLoading ? 'Setting up your account...' : 'Complete Setup'}
          </Button>
        </ButtonContainer>

        {onSkip && (
          <SkipButton onClick={onSkip}>
            Skip setup for now
          </SkipButton>
        )}
      </form>
    </StepContainer>
  );
};

export default OnboardingStep;